export type TKeyboardListenerTarget = {
  addEventListener: typeof addEventListener
  removeEventListener: typeof removeEventListener
}

export interface KeyboardEventListenerOptions {
  target: TKeyboardListenerTarget
  preventDefault?: boolean
}

export type TListenerRule = {
  keys: string[]
  keyCodes: number[]
  callback: () => void
  isCtrl: boolean
}

/**
 * Define key combinations to listen to
 * @example:
 * const keyboard = new KeyboardEventListener({ target: window })
 *
 * // Define your patterns
 * keyboard.on('up', () => console.log('go up'))
 * keyboard.on('down', () => console.log('go down'))
 * keyboard.on('cmd+s', () => console.log('save!'))
 *
 * // Register event listeners on window
 * keyboard.listen()
 */
export class KeyboardEventListener {
  static getKeyCodeFromString(key: string) {
    for (let code in KeyboardEventListener.keyCodes) {
      if (KeyboardEventListener.keyCodes[code] === key) {
        return +code
      }
    }
  }

  static keyCodes: { [key: string]: string } = {
    3: 'break',
    8: 'backspace',
    9: 'tab',
    12: 'clear',
    13: 'enter',
    16: 'shift',
    17: 'ctrl',
    18: 'alt',
    19: 'pause/break',
    20: 'caps lock',
    27: 'escape',
    28: 'conversion',
    29: 'non-conversion',
    32: 'spacebar',
    33: 'page up',
    34: 'page down',
    35: 'end',
    36: 'home ',
    37: 'left',
    38: 'up',
    39: 'right',
    40: 'down',
    41: 'select',
    42: 'print',
    43: 'execute',
    44: 'Print Screen',
    45: 'insert ',
    46: 'delete',
    48: '0',
    49: '1',
    50: '2',
    51: '3',
    52: '4',
    53: '5',
    54: '6',
    55: '7',
    56: '8',
    57: '9',
    58: ':',
    59: ';',
    65: 'a',
    66: 'b',
    67: 'c',
    68: 'd',
    69: 'e',
    70: 'f',
    71: 'g',
    72: 'h',
    73: 'i',
    74: 'j',
    75: 'k',
    76: 'l',
    77: 'm',
    78: 'n',
    79: 'o',
    80: 'p',
    81: 'q',
    82: 'r',
    83: 's',
    84: 't',
    85: 'u',
    86: 'v',
    87: 'w',
    88: 'x',
    89: 'y',
    90: 'z',
    91: 'cmd',
    93: 'cmd',
    224: 'cmd',
    112: 'f1 ',
    113: 'f2 ',
    114: 'f3 ',
    115: 'f4 ',
    116: 'f5 ',
    117: 'f6 ',
    118: 'f7 ',
    119: 'f8 ',
    120: 'f9 ',
    121: 'f10',
    122: 'f11',
    123: 'f12',
    124: 'f13',
    125: 'f14',
    126: 'f15',
    127: 'f16',
    128: 'f17',
    129: 'f18',
    130: 'f19',
    131: 'f20',
    132: 'f21',
    133: 'f22',
    134: 'f23',
    135: 'f24',
    170: '*',
    171: '~ + * key',
    186: ';',
    187: '=',
    188: ',',
    189: 'p ',
    190: '.',
  }

  public static keyupEvtName: 'keyup' = 'keyup'
  public static keydownEvtName: 'keydown' = 'keydown'
  public static defaults: Partial<KeyboardEventListenerOptions> = {
    preventDefault: true,
  }

  public options: KeyboardEventListenerOptions
  public target: TKeyboardListenerTarget
  public isListening = false
  public keysDown: number[] = []
  public rules: TListenerRule[] = []

  constructor(options: KeyboardEventListenerOptions) {
    this.options = Object.assign({}, KeyboardEventListener.defaults, options)
    this.target = this.options.target
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
  }

  on(pattern: string, callback: () => void) {
    const ctrlPattern = 'ctrl+'
    const isCtrl = pattern.startsWith(ctrlPattern)

    if (isCtrl) {
      pattern = pattern.slice(ctrlPattern.length)
    }

    const keys = pattern.split('+')
    const keyCodes = keys.map(key =>
      KeyboardEventListener.getKeyCodeFromString(key),
    )
    this.rules.push({ keys, keyCodes, callback, isCtrl })

    return this
  }

  listen() {
    this.isListening = true

    this.target.addEventListener(
      KeyboardEventListener.keyupEvtName,
      this.onKeyUp,
    )

    this.target.addEventListener(
      KeyboardEventListener.keydownEvtName,
      this.onKeyDown,
    )

    return this
  }

  stop() {
    this.isListening = false

    this.target.removeEventListener(
      KeyboardEventListener.keyupEvtName,
      this.onKeyUp,
    )

    this.target.removeEventListener(
      KeyboardEventListener.keydownEvtName,
      this.onKeyDown,
    )

    return this
  }

  getRulesThatShouldTrigger(e: KeyboardEvent) {
    const keysDownHash = this.keysDown.reduce(
      (obj, code) => Object.assign(obj, { [code]: true }),
      {},
    )

    return (
      this.rules
        // Remove ctrl rules when the ctrlKey isn't pressed
        .filter(rule => !(rule.isCtrl && !e.ctrlKey))
        // Only those rules whose head is _this_ event
        .filter(rule => {
          const lastKey = rule.keyCodes[rule.keyCodes.length - 1]
          return lastKey === e.keyCode
        })
        .filter(rule => {
          return rule.keyCodes
            .slice(0, rule.keyCodes.length - 1)
            .every(code => code in keysDownHash)
        })
    )
  }

  onKeyUp(e: KeyboardEvent) {
    this.keysDown = this.keysDown.filter(code => code != e.keyCode)
  }

  onKeyDown(e: KeyboardEvent) {
    const rules = this.getRulesThatShouldTrigger(e)

    if (rules.length > 0) {
      if (this.options.preventDefault) e.preventDefault()
      rules.forEach(rule => rule.callback())
      return
    }

    if (this.keysDown.indexOf(e.keyCode) == -1) {
      this.keysDown.push(e.keyCode)
    }
  }
}



// WEBPACK FOOTER //
// ./src/lib/KeyboardEventListener.ts